import { useEffect, useState } from "react";
import { askQuestion } from "../../../api/voiceApi.js";
import { useVoiceRecording } from "./hooks/useVoiceRecording.js";
import { useAudioVisualization } from "./hooks/useAudioVisualization.js";
import VoiceVisualizer from "./VoiceVisualizer.jsx";
import VoiceTranscript from "./VoiceTranscript.jsx";

export default function VoiceModal({ isOpen, onClose, theme, restaurant, userId, table, onAddMessages }) {
    const [transcript, setTranscript] = useState("");
    const [isPlayingAudio, setIsPlayingAudio] = useState(false);
    const [isWaitingResponse, setIsWaitingResponse] = useState(false);

    const {
        isRecording,
        isConnecting,
        recordingTime,
        startRecording: startVoiceRecording,
        finalizeAndClose,
        cleanup: cleanupRecording,
    } = useVoiceRecording(setTranscript);

    const {
        audioLevel,
        startVisualization,
        stopVisualization
    } = useAudioVisualization();

    useEffect(() => {
        if (isOpen) {
            startRecording();
        }

        return () => {
            cleanup();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const cleanup = () => {
        stopVisualization();
        cleanupRecording(true);
        setTranscript("");
    };

    const startRecording = async () => {
        setTranscript("");
        const stream = await startVoiceRecording(handleStop);
        if (stream) {
            startVisualization(stream);
        }
    };

    const handleStop = async () => {
        stopVisualization();
        await finalizeAndClose();
        cleanupRecording(true);
        setTranscript("");
    };

    const handleAsk = async () => {
        if (!transcript.trim()) return;

        stopVisualization();
        await finalizeAndClose();
        setIsWaitingResponse(true);

        try {
            const response = await askQuestion({
                restaurant: restaurant.slug,
                user: userId,
                table,
                question: transcript.trim(),
            });

            if (response) {
                if (onAddMessages) {
                    onAddMessages(response.question, response.answer);
                }

                setIsPlayingAudio(true);
                const audio = response.playAudio();

                if (audio) {
                    audio.addEventListener('ended', () => {
                        setIsPlayingAudio(false);
                    });

                    audio.addEventListener('error', () => {
                        setIsPlayingAudio(false);
                    });
                }
            }

            cleanupRecording(true);
            setTranscript("");
        } catch (error) {
            console.error("Error sending question:", error);
            alert("Failed to send question");
        } finally {
            setIsWaitingResponse(false);
        }
    };

    if (!isOpen) return null;

    const styles = {
        overlay: {
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
        },
        modal: {
            backgroundColor: theme.cardBg || "#1f1f1f",
            opacity: 1,
            borderRadius: "16px",
            padding: "32px",
            width: "90%",
            maxWidth: "420px",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
        },
        header: {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "24px",
        },
        title: {
            fontSize: "18px",
            fontWeight: "600",
            color: theme.text,
            opacity: 0.9,
        },
        closeBtn: {
            background: "none",
            border: "none",
            fontSize: "24px",
            cursor: "pointer",
            color: theme.text,
            padding: "0",
            width: "30px",
            height: "30px",
            opacity: 0.7,
            transition: "opacity 0.2s",
        },
        content: {
            marginBottom: "20px",
        },
        buttonRow: {
            display: "flex",
            gap: "12px",
            justifyContent: "center",
        },
        button: {
            padding: "12px 28px",
            borderRadius: "10px",
            border: "none",
            fontSize: "15px",
            fontWeight: "500",
            cursor: "pointer",
            transition: "all 0.2s",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        },
        startBtn: {
            backgroundColor: theme.buttonBg,
            color: theme.buttonText,
        },
        stopBtn: {
            backgroundColor: "#ff4444",
            color: "white",
        },
        askBtn: {
            backgroundColor: theme.buttonBg,
            color: theme.buttonText,
        },
        disabledBtn: {
            opacity: 0.4,
            cursor: "not-allowed",
        },
    };

    return (
        <div style={styles.overlay} onClick={onClose}>
            <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div style={styles.header}>
                    <div style={styles.title}>
                        {isWaitingResponse ? "Думаю, дейте 2 секунди" : isPlayingAudio ? "Відповідаю" : isRecording ? "Слухаю Вас" : "Вова офіціант"}
                    </div>
                    <button
                        style={styles.closeBtn}
                        onClick={onClose}
                        onMouseEnter={(e) => e.target.style.opacity = "1"}
                        onMouseLeave={(e) => e.target.style.opacity = "0.7"}
                    >
                        ×
                    </button>
                </div>

                <div style={styles.content}>
                    <VoiceVisualizer
                        isRecording={isRecording}
                        isPlayingAudio={isPlayingAudio}
                        audioLevel={audioLevel}
                        recordingTime={recordingTime}
                        theme={theme}
                    />

                    <VoiceTranscript
                        transcript={transcript}
                        isPlayingAudio={isPlayingAudio}
                        isConnecting={isConnecting}
                        isRecording={isRecording}
                        theme={theme}
                    />

                    <div style={styles.buttonRow}>
                        {isRecording || isConnecting ? (
                            <>
                                <button
                                    style={{ ...styles.button, ...styles.stopBtn }}
                                    onClick={handleStop}
                                    disabled={isConnecting}
                                >
                                     Стоп
                                </button>
                                <button
                                    style={{
                                        ...styles.button,
                                        ...styles.askBtn,
                                        ...(!transcript.trim() || isConnecting ? styles.disabledBtn : {}),
                                    }}
                                    onClick={handleAsk}
                                    disabled={!transcript.trim() || isConnecting}
                                >
                                    Запитати
                                </button>
                            </>
                        ) : (
                            <button
                                style={{ ...styles.button, ...styles.startBtn }}
                                onClick={startRecording}
                                disabled={isPlayingAudio}
                            >
                                Почати
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}